import { useFlags } from 'launchdarkly-react-client-sdk';
import useUI from 'src/hooks/use-ui';
import useCart from 'shared/hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useKioskSeen } from 'src/hooks/use-kiosk-seen';

// Same flag the admin "Manage Kiosks" UI uses (KIOSK_ORIGIN_IDENTIFICATION_FLAG in
// apps/admin/.../ordering/constants.ts) gates both the admin kiosk-naming UI and this hook.
export const KIOSK_ORIGIN_IDENTIFICATION_FLAG = 'ecomm.kiosk-origin-identification.rollout';

/**
 * Fires the kiosk liveness ping (`kioskSeen`) for an already-claimed device.
 *
 * ECOMM-12178's PIN is optional: a kiosk works as a normal storefront whether or not it's ever
 * been claimed with a PIN (see `/kiosks/[cName]/setup`, the dedicated route for entering one).
 * This hook therefore never blocks rendering -- it only reports "this device is still here" so
 * Manage Kiosks can show accurate claimed/last-seen status, and so a kiosk unclaimed/deleted by
 * an admin eventually has its stale local id cleared (see `useKioskSeen`'s `stillClaimed`
 * contract).
 *
 * Single source of truth -- previously this same condition and ping were duplicated per-route
 * via `useKioskPinGate`/`KioskPinGate`, and the standalone `/lock`, `/info`, `/checkout` routes
 * (not wrapped by `CoreMenuLayout`) had to remember to opt in individually. Any kiosk route that
 * isn't wrapped by `CoreMenuLayout` must still call this hook itself. `dispensaryId` is optional:
 * `CoreMenuLayout` already has it from its own `useDispensary()` call and passes it in to avoid a
 * second query; other callers fall back to fetching it here via the route's `cName`.
 */
export function useKioskLiveness(dispensaryId?: string | null): void {
  const UI = useUI();
  const flags = useFlags();
  const Cart = useCart();
  const { dispensary } = useDispensary();
  const isKioskOriginIdentificationEnabled = flags[KIOSK_ORIGIN_IDENTIFICATION_FLAG] ?? false;
  const storedKioskId = Cart?.originKioskId ?? null;
  const isKiosk = Boolean(UI.isKiosk) && isKioskOriginIdentificationEnabled;
  const resolvedDispensaryId = dispensaryId ?? dispensary?.id ?? null;

  useKioskSeen({ isKiosk, kioskId: storedKioskId, dispensaryId: resolvedDispensaryId });
}
